import { ProductInventoryUnit, ProductSupplierTransactionCheckItem } from '@gooduncles/gu-app-schema';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button, Input, Space, Tag, message } from 'antd';
import { Dayjs } from 'dayjs';
import { FC, useCallback, useMemo, useState } from 'react';
import { SupplierInboundItem } from 'src/reducers/supplierInboundReducer';
import { useAuth } from 'src/stores/auth-context';

import {
  formatNumberForAgCell,
  onCellValueChangedWithUpdate,
  onValueSetterWithValidation,
} from 'src/utils/ag-grid-util';
import { SupplierInboundData, requestSupplierInbound } from 'src/utils/product-inventory-util';

import styles from './RequestSupplierInboundTable.module.scss';

// 셀 편집 후 포커스를 유지하는 범용 함수
const refocusCell = (params: any, fieldName: string) => {
  if (params.node && params.node.rowIndex !== null) {
    setTimeout(() => {
      params.api.setFocusedCell(params.node.rowIndex, fieldName);
    }, 100);
  }
};

type SupplierInboundRowData = SupplierInboundItem & {
  ratio: number;
  cost: number;
  totalPrice: number;
};

const productInventoryPath = 'productInventory';
const onCellValueChanged = onCellValueChangedWithUpdate<SupplierInboundRowData>('거래처 입고');

const defaultColDef: ColDef<SupplierInboundRowData> = {
  sortable: true,
  resizable: true,
  filter: true,
  suppressMovable: true,
  suppressNavigable: false,
};

const totalRowData: SupplierInboundRowData = {
  id: 'total',
  key: 'total',
  inventoryId: '',
  inventoryName: '합계',
  unit: ProductInventoryUnit.EA,
  ratio: 0,
  requestValue: 0,
  cost: 0,
  totalPrice: 0,
};

const isDataRow = (params: any) => params.data?.id !== 'total';

interface RequestSupplierInboundTableProps {
  date: Dayjs;
  productSupplierId: string;
  rowData: SupplierInboundRowData[];
  updateItem: (id: string, data: Partial<SupplierInboundData>) => void;
  removeItem: (id: string) => void;
  clearItems: () => void;
}

const RequestSupplierInboundTable: FC<RequestSupplierInboundTableProps> = ({
  date,
  productSupplierId,
  rowData,
  updateItem,
  removeItem,
  clearItems,
}) => {
  const { user } = useAuth();
  const [selectedRows, setSelectedRows] = useState<SupplierInboundRowData[]>([]);
  const [ref, setRef] = useState<AgGridReact<SupplierInboundRowData> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [remarks, setRemarks] = useState<string[]>([]);
  const [remarkInput, setRemarkInput] = useState<string>('');

  const handleDeleteSelected = useCallback(() => {
    if (selectedRows.length === 0) {
      message.warning('삭제할 행을 선택해주세요.');
      return;
    }
    selectedRows.forEach((row) => removeItem(row.id));
  }, [removeItem, selectedRows]);

  const columnDefs: ColDef<SupplierInboundRowData>[] = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: isDataRow,
        width: 54,
        headerName: '',
        field: 'selection',
        suppressMenu: true,
        sortable: false,
        filter: false,
        resizable: false,
      },
      {
        headerName: '재고명',
        field: 'inventoryName',
        width: 240,
      },
      {
        headerName: '단위',
        field: 'unit',
        width: 100,
      },
      {
        headerName: '수량',
        field: 'requestValue',
        width: 100,
        editable: isDataRow,
        valueFormatter: (params) => (isDataRow(params) ? formatNumberForAgCell(params) : ''),
        type: 'numericColumn',
        valueParser: (params) => Number(params.newValue),
        onCellValueChanged: (params) => {
          updateItem(params.data.id, {
            requestValue: Number(params.newValue),
          });
          refocusCell(params, 'requestValue');
        },
      },
      {
        headerName: '매입단가',
        field: 'cost',
        width: 100,
        editable: isDataRow,
        valueFormatter: (params) => (isDataRow(params) ? formatNumberForAgCell(params) : ''),
        type: 'numericColumn',
        valueSetter: (params) => onValueSetterWithValidation(params, ['inventoryId'], undefined, { type: 'number' }),
        onCellValueChanged: (params) => {
          /**
           * 재고의 매입단가를 변경하면 functions에서 상품의 매입단가도 변경한다.
           */
          onCellValueChanged(
            params,
            productInventoryPath,
            params.data.inventoryId,
            `uom.${params.data.unit}.cost`
          ).then(() => refocusCell(params, 'cost'));
        },
      },
      {
        headerName: '매입가',
        field: 'totalPrice',
        width: 100,
        valueFormatter: formatNumberForAgCell,
        type: 'numericColumn',
      },
    ],
    [updateItem]
  );

  const handleAddRemark = useCallback(() => {
    if (!remarkInput.trim()) {
      message.warning('특이사항을 입력해주세요.');
      return;
    }
    setRemarks((prev) => [...prev, remarkInput.trim()]);
    setRemarkInput('');
  }, [remarkInput]);

  const handleRemoveRemark = useCallback((e: React.MouseEvent<HTMLElement>, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setRemarks((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault(); // 기본 동작 방지
        if (e.nativeEvent.isComposing) {
          return;
        }
        handleAddRemark();
      }
    },
    [handleAddRemark]
  );

  const handleSave = useCallback(async () => {
    if (!ref?.api) return;
    if (!user?._id) {
      message.error('사용자 정보를 찾을 수 없습니다.');
      return;
    }

    // 동기화된 rowData 사용
    const items = rowData.filter((row) => row.id !== 'total');
    setLoading(true);

    // 특이사항을 ProductSupplierTransactionCheckItem 형태로 변환
    const checkList: ProductSupplierTransactionCheckItem[] = remarks.map((remark) => ({
      content: remark,
      completed: false,
    }));

    const response = await requestSupplierInbound(productSupplierId, date, user?._id, items, checkList);
    if (response.data.result === 'success') {
      message.success('거래처 입고 요청이 완료되었습니다.');
    } else {
      message.error(`거래처 입고 요청에 실패했습니다. ${response.data.reason}`);
    }
    setRemarks([]);
    setRemarkInput('');
    ref?.api.deselectAll();
    setSelectedRows([]);
    clearItems();
    setLoading(false);
  }, [ref?.api, rowData, productSupplierId, date, user?._id, remarks, clearItems]);

  return (
    <div className='ag-theme-alpine height100 flexColumn'>
      <div className='flexRow flexSpaceBetween'>
        <Button danger onClick={handleDeleteSelected} disabled={selectedRows.length === 0}>
          선택 삭제
        </Button>
        <Button
          type='primary'
          onClick={handleSave}
          disabled={rowData.filter((row) => row.id !== 'total').length === 0}
          loading={loading}>
          입고 처리
        </Button>
      </div>
      <AgGridReact
        ref={setRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowSelection='multiple'
        suppressRowClickSelection={true}
        pinnedBottomRowData={[
          {
            ...totalRowData,
            totalPrice: rowData.reduce((acc, row) => acc + row.totalPrice, 0),
          },
        ]}
        onSelectionChanged={(e) => {
          const selectedRows = e.api.getSelectedRows().filter((row) => row.id !== 'total');
          setSelectedRows(selectedRows);
        }}
        enableCellTextSelection={true}
      />

      {/* 특이사항 Form */}
      <div className={styles.remarksContainer}>
        <p>특이사항</p>
        <Space direction='vertical' className='width100'>
          <Space.Compact className='width100'>
            <Input
              placeholder='특이사항을 입력하세요'
              value={remarkInput}
              onChange={(e) => setRemarkInput(e.target.value)}
              onPressEnter={handleKeyDown}
            />
            <Button loading={loading} onClick={handleAddRemark}>
              추가
            </Button>
          </Space.Compact>

          <div>
            {remarks.length > 0 ? (
              remarks.map((remark, index) => (
                <Tag key={index} closable onClose={(e) => handleRemoveRemark(e, index)} className={styles.remarkTag}>
                  {remark}
                </Tag>
              ))
            ) : (
              <div className={styles.emptyRemarks}>등록된 특이사항이 없습니다.</div>
            )}
          </div>
        </Space>
      </div>
    </div>
  );
};

export default RequestSupplierInboundTable;
